import { readFileSync, writeFileSync, mkdirSync, existsSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

const AUGMENTED_DIR = join(homedir(), '.augmented');
const CONFIG_PATH = join(AUGMENTED_DIR, 'config.json');

function ensureAugmentedDir(): void {
  if (!existsSync(AUGMENTED_DIR)) {
    mkdirSync(AUGMENTED_DIR, { recursive: true });
  }
}

// ---------------------------------------------------------------------------
// Shell profile env loader — reads AGT_* vars from shell profile if not in env
// ---------------------------------------------------------------------------

/**
 * If AGT_HOST or AGT_API_KEY are missing from the environment, try to
 * extract them from the user's shell profile. This handles the case where
 * `agt setup` wrote the vars but the user hasn't sourced the profile yet.
 */
export function reloadFromShellProfile(): void {
  return loadFromShellProfile(true);
}

function loadFromShellProfile(force = false): void {
  if (!force && process.env['AGT_HOST'] && process.env['AGT_API_KEY']) return;

  const shell = process.env['SHELL'] ?? '';
  const home = homedir();
  const candidates = shell.includes('zsh')
    ? [join(home, '.zshrc'), join(home, '.zprofile')]
    : shell.includes('fish')
      ? [join(home, '.config', 'fish', 'config.fish')]
      : [join(home, '.bashrc'), join(home, '.bash_profile')];

  for (const profile of candidates) {
    try {
      const content = readFileSync(profile, 'utf-8');
      for (const key of ['AGT_HOST', 'AGT_API_KEY', 'AGT_TEAM'] as const) {
        if (!force && process.env[key]) continue;
        // Match active (non-comment) lines only.
        const match = content
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0 && !line.startsWith('#'))
          .map((line) =>
            line.match(
              new RegExp(
                `^(?:export\\s+${key}\\s*=\\s*["']([^"']+)["']|set\\s+-gx\\s+${key}\\s+["']([^"']+)["'])$`,
              ),
            ),
          )
          .find(Boolean);
        if (match) {
          process.env[key] = match[1] ?? match[2];
        }
      }
    } catch {
      // Profile doesn't exist
    }
    if (process.env['AGT_HOST'] && process.env['AGT_API_KEY']) break;
  }
}

// Auto-load on module import
loadFromShellProfile();

// ---------------------------------------------------------------------------
// API key
// ---------------------------------------------------------------------------

/**
 * Returns the host API key (`tlk_...`) from `AGT_API_KEY` env var, or null.
 */
export function getApiKey(): string | null {
  return process.env['AGT_API_KEY'] ?? null;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AugmentedConfig {
  active_team?: string; // team slug
}

export function getConfig(): AugmentedConfig {
  try {
    const raw = readFileSync(CONFIG_PATH, 'utf-8');
    return JSON.parse(raw) as AugmentedConfig;
  } catch {
    return {};
  }
}

export function saveConfig(config: AugmentedConfig): void {
  ensureAugmentedDir();
  writeFileSync(CONFIG_PATH, JSON.stringify(config, null, 2));
}

export function getActiveTeam(): string | undefined {
  // Check env var first (headless / agent mode)
  const envTeam = process.env['AGT_TEAM'];
  if (envTeam) return envTeam;

  return getConfig().active_team;
}

export function setActiveTeam(slug: string): void {
  const config = getConfig();
  config.active_team = slug;
  saveConfig(config);
}

// ---------------------------------------------------------------------------
// API host
// ---------------------------------------------------------------------------

/**
 * The Production API URL. Use this when a call site needs to *explicitly*
 * target prod regardless of the operator's `AGT_HOST` (impersonate's redeem
 * exchange, ENG-5773). NEVER use as a silent fallback for unset AGT_HOST —
 * that's the ENG-5831 bug: a non-prod-stage operator who hasn't sourced their
 * profile yet would silently exchange a stage token against prod.
 */
export const PROD_AGT_HOST = 'https://api.augmented.team';

/**
 * Actionable error message thrown by `getHost()` / `requireHost()` when
 * `AGT_HOST` is not in the environment (and the shell-profile loader didn't
 * find it either). Lists every legitimate way to set it, plus a pointer to
 * the `--api-host` flag for first-time setup (where AGT_HOST is by
 * definition not yet on disk).
 */
export const AGT_HOST_UNSET_MESSAGE =
  'AGT_HOST is not set. Set it to the API URL for your stage before running this command:\n' +
  '  Production:        export AGT_HOST=https://api.augmented.team\n' +
  '  Non-prod stage:    export AGT_HOST=https://<stage>.api.staging.augmented.team\n' +
  '  Local development: export AGT_HOST=http://api.agt.localhost:1355\n' +
  '\n' +
  'For first-time host setup, pass --api-host <url> to `agt setup` instead — the ' +
  'setup command does NOT silently default to prod (ENG-5831).';

/**
 * Augmented API server URL.
 *
 * Reads `AGT_HOST` from the environment. Throws if unset — ENG-5831
 * established that a silent prod default routes non-prod-stage callers to
 * prod and looks like an opaque connectivity failure. Treats blank /
 * whitespace values as unset so a stray `export AGT_HOST=` can't propagate
 * an invalid host.
 */
export function getHost(): string {
  const envHost = process.env['AGT_HOST']?.trim();
  if (!envHost) {
    throw new Error(AGT_HOST_UNSET_MESSAGE);
  }
  return envHost;
}

export function requireHost(): string {
  return getHost();
}
